import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ItemChip.css';

// Spritesheet registry - maps category to sprite positions and metadata
// Format: /assets/weapons-{rarity}/spritesheet.png
const SPRITESHEET_REGISTRY = {
  // weapons-common
  'weapons-common': {
    size: { w: 32, h: 32 }, // Update when you add sprites
    sprites: {},
  },
  // weapons-uncommon
  'weapons-uncommon': {
    size: { w: 32, h: 32 },
    sprites: {},
  },
  // weapons-rare
  'weapons-rare': {
    size: { w: 32, h: 32 },
    sprites: {},
  },
  // weapons-epic
  'weapons-epic': {
    size: { w: 32, h: 32 },
    sprites: {},
  },
  // weapons-legendary
  'weapons-legendary': {
    size: { w: 96, h: 32 }, // 3 sprites × 32px = 96px wide
    sprites: {
      'firebrand.png': { x: 0, y: 0 },
      'frostblade.png': { x: -32, y: 0 },
      'natureweaver.png': { x: -64, y: 0 },
    },
  },
  // weapons-mythic (firebrand, frostblade, natureweaver promoted to mythic tier)
  'weapons-mythic': {
    size: { w: 96, h: 32 }, // 3 sprites × 32px = 96px wide
    sprites: {
      'firebrand.png': { x: 0, y: 0 },
      'frostblade.png': { x: -32, y: 0 },
      'natureweaver.png': { x: -64, y: 0 },
    },
  },
};

// Get spritesheet category from item slot + rarity
function getSpritesheetCategory(item) {
  // If item specifies a custom spritesheet, use that
  if (item.spritesheet) return item.spritesheet;
  // Otherwise, build from slot + rarity (e.g., "weapons-legendary")
  const slot = item.slot === 'weapon' ? 'weapons' : item.slot;
  return `${slot}-${item.rarity}`;
}

// Get spritesheet path for a category
function getSpritesheetPath(category) {
  return `/assets/${category}/spritesheet.png`;
}

// Get sprite position at 1x scale (32px)
function getSpritePosition(spriteName, category) {
  const sheet = SPRITESHEET_REGISTRY[category];
  if (!sheet) return '0px 0px';
  const pos = sheet.sprites[spriteName] || { x: 0, y: 0 };
  return `${pos.x}px ${pos.y}px`;
}

// Get sprite position scaled (for larger displays like tooltips)
function getScaledSpritePosition(spriteName, scale, category) {
  const sheet = SPRITESHEET_REGISTRY[category];
  if (!sheet) return '0px 0px';
  const pos = sheet.sprites[spriteName] || { x: 0, y: 0 };
  return `${pos.x * scale}px ${pos.y * scale}px`;
}

// Get spritesheet size (scaled)
function getSpritesheetSize(category, scale = 1) {
  const sheet = SPRITESHEET_REGISTRY[category];
  if (!sheet) return '32px 32px';
  return `${sheet.size.w * scale}px ${sheet.size.h * scale}px`;
}

/**
 * ItemChip - MapleStory-style item display component
 *
 * Shows item icon with compact side-panel tooltip on hover
 * Works with draggable elements by using wrapper hover events
 */
const ItemChip = ({
  item,
  amount = null, // Optional: show quantity badge
  size = 'medium', // 'small', 'medium', 'large'
  showTooltip = true,
  onClick = null,
  className = '',
  equippedItem = null, // Currently equipped item in same slot for comparison
  playerStats = null, // Player's primary stats for requirement checking
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipReady, setTooltipReady] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const chipRef = useRef(null);
  const tooltipRef = useRef(null);

  // Tooltip positioning with overflow detection
  const handleMouseEnter = (e) => {
    if (!showTooltip || !chipRef.current) return;

    // Calculate position immediately before showing
    const chipRect = chipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 10;

    // Estimate tooltip size (will be refined after render)
    const estimatedWidth = 320;
    const estimatedHeight = 400;

    // Default: position to the right of the chip
    let x = chipRect.right + gap;
    let y = chipRect.top;

    // Check right overflow - if tooltip goes off right edge, position to left
    if (x + estimatedWidth > viewportWidth - gap) {
      x = chipRect.left - estimatedWidth - gap;
    }

    // Check left overflow - if still off screen, clamp to right edge
    if (x < gap) {
      x = viewportWidth - estimatedWidth - gap;
    }

    // Check bottom overflow - adjust upward if needed
    if (y + estimatedHeight > viewportHeight - gap) {
      y = viewportHeight - estimatedHeight - gap;
    }

    // Check top overflow
    if (y < gap) {
      y = gap;
    }

    setTooltipPosition({ x, y });
    setTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
    setTooltipReady(false);
  };

  // Refine position after tooltip renders with actual dimensions
  React.useEffect(() => {
    if (tooltipVisible && tooltipRef.current && chipRef.current) {
      const chip = chipRef.current;
      const tooltip = tooltipRef.current;
      const chipRect = chip.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 10;

      let x = chipRect.right + gap;
      let y = chipRect.top;

      if (x + tooltipRect.width > viewportWidth - gap) {
        x = chipRect.left - tooltipRect.width - gap;
      }
      if (x < gap) {
        x = viewportWidth - tooltipRect.width - gap;
      }
      if (y + tooltipRect.height > viewportHeight - gap) {
        y = viewportHeight - tooltipRect.height - gap;
      }
      if (y < gap) {
        y = gap;
      }

      setTooltipPosition({ x, y });
      // Make tooltip visible after position is calculated
      requestAnimationFrame(() => setTooltipReady(true));
    }
  }, [tooltipVisible]);

  // Rarity color mapping
  const rarityColors = {
    common: '#9ca3af',     // Gray
    uncommon: '#10b981',   // Green
    rare: '#3b82f6',       // Blue
    epic: '#a855f7',       // Purple
    legendary: '#f59e0b',  // Amber/Gold
    mythic: '#e879f9',     // Pink (base color, rainbow effect in CSS)
  };

  const rarityColor = rarityColors[item.rarity] || rarityColors.common;
  const isMythic = item.rarity === 'mythic';

  // Render tooltip content
  const renderTooltip = () => {
    if (!showTooltip || !tooltipVisible) return null;

    return (
      <div
        ref={tooltipRef}
        className={`item-tooltip-ms ${isMythic ? 'mythic-tooltip' : ''}`}
        style={{
          borderColor: isMythic ? 'transparent' : rarityColor,
          position: 'fixed',
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          opacity: tooltipReady ? 1 : 0,
        }}
      >
          {/* Star Force display - MapleStory style, ABOVE the header */}
          {item.slot && (
            <div className={`ms-starforce-display ${(item.starForce || 0) === 0 ? 'no-stars' : ''} ${(item.starForce || 0) >= 15 ? 'max-stars' : ''}`}>
              {/* Row 1: Stars 1-10 (two groups of 5) */}
              <div className="starforce-row">
                <div className="starforce-group">
                  {[0, 1, 2, 3, 4].map(i => (
                    <span key={i} className={`sf-star ${i < (item.starForce || 0) ? 'filled' : 'empty'}`}>
                      {i < (item.starForce || 0) ? '★' : '☆'}
                    </span>
                  ))}
                </div>
                <div className="starforce-group">
                  {[5, 6, 7, 8, 9].map(i => (
                    <span key={i} className={`sf-star ${i < (item.starForce || 0) ? 'filled' : 'empty'}`}>
                      {i < (item.starForce || 0) ? '★' : '☆'}
                    </span>
                  ))}
                </div>
              </div>
              {/* Row 2: Stars 11-15 (one group of 5) */}
              <div className="starforce-row">
                <div className="starforce-group center">
                  {[10, 11, 12, 13, 14].map(i => (
                    <span key={i} className={`sf-star ${i < (item.starForce || 0) ? 'filled' : 'empty'} ${i >= 10 ? 'high-star' : ''}`}>
                      {i < (item.starForce || 0) ? '★' : '☆'}
                    </span>
                  ))}
                </div>
              </div>
              {(item.starForce || 0) === 0 && (
                <div className="starforce-enhance-hint">Right-click to enhance</div>
              )}
            </div>
          )}

          {/* Header with icon and name */}
          <div className="ms-tooltip-header">
            {hasSprite ? (() => {
              const category = getSpritesheetCategory(item);
              return (
                <div
                  className="ms-tooltip-sprite"
                  style={{
                    backgroundImage: `url(${getSpritesheetPath(category)})`,
                    backgroundPosition: getScaledSpritePosition(item.sprite, 2, category),
                    backgroundSize: getSpritesheetSize(category, 2),
                    width: '64px',
                    height: '64px',
                    backgroundRepeat: 'no-repeat',
                    imageRendering: 'pixelated',
                  }}
                />
              );
            })() : (
              <span className="ms-tooltip-icon">{item.icon}</span>
            )}
            <div className="ms-tooltip-title">
              <div className="ms-tooltip-name" style={{ color: rarityColor }}>
                {item.name}
              </div>
              <div className="ms-tooltip-rarity" style={{ color: rarityColor }}>
                {item.rarity.toUpperCase()}
              </div>
            </div>
            {/* Scroll enhancement level - bottom right of header */}
            {item.appliedScrolls?.length > 0 && (
              <span className="ms-scroll-level">+{item.appliedScrolls.length}</span>
            )}
          </div>

          {/* Quality Bar (Lost Ark style) - Equipment only */}
          {item.quality !== undefined && item.slot && (
            <div className="ms-tooltip-quality">
              <div className="quality-label-row">
                <span className="quality-label">Quality</span>
                <span className="quality-value" style={{ color: getQualityColor(item.quality) }}>
                  {item.quality}% {getQualityLabel(item.quality)}
                </span>
              </div>
              <div className="quality-bar-container">
                <div
                  className="quality-bar-fill"
                  style={{
                    width: `${item.quality}%`,
                    backgroundColor: getQualityColor(item.quality),
                    boxShadow: `0 0 8px ${getQualityColor(item.quality)}80`
                  }}
                />
              </div>
            </div>
          )}

          {/* Description */}
          {item.description && (
            <div className="ms-tooltip-desc">
              "{item.description}"
            </div>
          )}

          {/* Equipment Type & Slot */}
          {item.slot && (
            <div className="ms-tooltip-type">
              <span className="equipment-type">{getEquipmentType(item)}</span>
              <span className="equipment-slot">{formatSlotName(item.slot)}</span>
              {item.levelReq > 0 && (
                <span className="equipment-level-req">Lv. {item.levelReq}</span>
              )}
            </div>
          )}

          {/* Category for non-equipment */}
          {!item.slot && item.category && (
            <div className="ms-tooltip-meta">
              <span>Type: {formatCategoryName(item.category)}</span>
            </div>
          )}

          {/* Equipment Requirements (MapleStory style - 2x2 grid, always show all stats) */}
          {item.slot && (
            <div className="ms-tooltip-requirements">
              <div className="requirements-header">Required Stats</div>
              <div className="requirements-grid">
                <div className={`req-stat ${playerStats && item.requirements?.STR && playerStats.STR < item.requirements.STR ? 'req-unmet' : 'req-met'}`}>
                  <span className="req-label">STR</span>
                  <span className="req-value">{item.requirements?.STR || 0}</span>
                </div>
                <div className={`req-stat ${playerStats && item.requirements?.DEX && playerStats.DEX < item.requirements.DEX ? 'req-unmet' : 'req-met'}`}>
                  <span className="req-label">DEX</span>
                  <span className="req-value">{item.requirements?.DEX || 0}</span>
                </div>
                <div className={`req-stat ${playerStats && item.requirements?.INT && playerStats.INT < item.requirements.INT ? 'req-unmet' : 'req-met'}`}>
                  <span className="req-label">INT</span>
                  <span className="req-value">{item.requirements?.INT || 0}</span>
                </div>
                <div className={`req-stat ${playerStats && item.requirements?.LUK && playerStats.LUK < item.requirements.LUK ? 'req-unmet' : 'req-met'}`}>
                  <span className="req-label">LUK</span>
                  <span className="req-value">{item.requirements?.LUK || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Stats section with comparison and star force bonuses */}
          {item.stats && Object.keys(item.stats).length > 0 && (
            <div className="ms-tooltip-stats">
              {Object.entries(item.stats).map(([stat, value]) => {
                // item.stats already contains quality-scaled values from addToInventory
                // No need to apply quality scaling again here
                const displayValue = value;

                // Skip zero-value stats
                if (!displayValue || displayValue === 0) return null;

                // Get star force bonus for this stat
                const sfBonus = item.starForceStats?.[stat] || 0;

                // Get scroll bonus for this stat
                const scrollBonus = item.scrollStats?.[stat] || 0;

                // Equipped item stats are also already quality-scaled
                const equippedValue = equippedItem?.stats?.[stat] || 0;

                const difference = displayValue - equippedValue;
                const showComparison = equippedItem && difference !== 0;

                // Format value based on stat type
                const formatValue = (val, statName) => {
                  if (statName === 'critChance' || statName === 'critDamage' || statName === 'evasion') {
                    return `${Math.round(val * 100)}%`;
                  }
                  return Math.floor(val);
                };

                return (
                  <div key={stat} className="ms-stat-row">
                    <span className="ms-stat-label">{formatStatName(stat)}</span>
                    <div className="ms-stat-values">
                      <span className="ms-stat-value">+{formatValue(displayValue, stat)}</span>
                      {sfBonus > 0 && (
                        <span className="ms-stat-sf-bonus">
                          (+{formatValue(sfBonus, stat)})
                        </span>
                      )}
                      {scrollBonus > 0 && (
                        <span className="ms-stat-scroll-bonus">
                          (+{formatValue(scrollBonus, stat)})
                        </span>
                      )}
                      {showComparison && (
                        <span className={`ms-stat-diff ${difference > 0 ? 'stat-increase' : 'stat-decrease'}`}>
                          ({difference > 0 ? '+' : ''}{formatValue(difference, stat)})
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Star Force bonus stats that aren't in base stats */}
              {item.starForceStats && Object.entries(item.starForceStats).map(([stat, value]) => {
                // Skip if already shown in base stats
                if (item.stats?.[stat]) return null;
                if (value <= 0) return null;

                return (
                  <div key={`sf-${stat}`} className="ms-stat-row sf-only-stat">
                    <span className="ms-stat-label">{formatStatName(stat)}</span>
                    <div className="ms-stat-values">
                      <span className="ms-stat-sf-bonus">
                        +{stat === 'critChance' || stat === 'critDamage' || stat === 'allStats' ? `${Math.round(value * 100)}%` : value}
                      </span>
                    </div>
                  </div>
                );
              })}
              {/* Scroll (Forging) bonus stats */}
              {item.scrollStats && Object.entries(item.scrollStats).map(([stat, value]) => {
                // Skip if already shown in base stats or star force stats
                if (item.stats?.[stat]) return null;
                if (item.starForceStats?.[stat]) return null;
                if (value <= 0) return null;

                return (
                  <div key={`scroll-${stat}`} className="ms-stat-row scroll-only-stat">
                    <span className="ms-stat-label">{formatStatName(stat)}</span>
                    <div className="ms-stat-values">
                      <span className="ms-stat-scroll-bonus">
                        +{stat === 'critChance' ? `${Math.round(value * 100)}%` : value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Potential Display (Cubing system) */}
          {item.slot && (
            <div className="ms-tooltip-potential">
              {item.potentialGrade ? (
                <>
                  <div
                    className="potential-header"
                    style={{ '--potential-color': getPotentialGradeColor(item.potentialGrade) }}
                  >
                    <span className="potential-icon">{getPotentialGradeIcon(item.potentialGrade)}</span>
                    <span className="potential-grade">{item.potentialGrade.toUpperCase()} Potential</span>
                  </div>
                  <div className="potential-lines">
                    {(item.potentialLines || []).map((line, i) => (
                      <div
                        key={i}
                        className="potential-line"
                        style={{ '--potential-color': getPotentialGradeColor(item.potentialGrade) }}
                      >
                        {formatPotentialLineDisplay(line)}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="potential-none">
                  <span className="potential-empty-icon">⚪</span>
                  <span className="potential-empty-text">No Potential</span>
                  <div className="potential-enhance-hint">Use cubes to reveal potential</div>
                </div>
              )}
            </div>
          )}

          {/* Gem Slots Display (Forging system) - at the very bottom */}
          {item.slot && item.maxGemSlots !== undefined && (
            <div className="ms-tooltip-scrolls">
              <div className="gem-slots-header">
                Gem Slots ({(item.appliedScrolls?.length || 0) + (item.failedSlots || 0)}/{item.maxGemSlots})
              </div>
              <div className="gem-slots-display">
                {/* Socketed slots */}
                {(item.appliedScrolls || []).map((scroll, i) => (
                  <span
                    key={`used-${i}`}
                    className="gem-slot socketed"
                    title={`${scroll.stat} +${scroll.stat === 'critChance' ? Math.round(scroll.value * 100) + '%' : scroll.value}`}
                  >
                    {scroll.tier}
                  </span>
                ))}
                {/* Failed slots */}
                {Array.from({ length: item.failedSlots || 0 }).map((_, i) => (
                  <span key={`failed-${i}`} className="gem-slot failed">X</span>
                ))}
                {/* Empty slots */}
                {Array.from({
                  length: (item.maxGemSlots || 0) - (item.appliedScrolls?.length || 0) - (item.failedSlots || 0)
                }).map((_, i) => (
                  <span key={`empty-${i}`} className="gem-slot empty"></span>
                ))}
              </div>
              {(item.appliedScrolls?.length || 0) === 0 && (item.failedSlots || 0) === 0 && (
                <div className="gem-enhance-hint">Right-click to forge</div>
              )}
            </div>
          )}

        </div>
    );
  };

  // Determine if item has a sprite
  const hasSprite = item.sprite && typeof item.sprite === 'string';

  return (
    <>
      <div
        ref={chipRef}
        className={`item-chip item-chip-${size} ${hasSprite ? 'has-sprite' : ''} ${className}`}
        data-rarity={item.rarity}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ borderColor: rarityColor, pointerEvents: 'auto' }}
      >
        {hasSprite ? (() => {
          const category = getSpritesheetCategory(item);
          const spriteScale = size === 'large' ? 1.5 : 1;
          const spriteSize = 32 * spriteScale;
          return (
            <div
              className="item-chip-sprite"
              data-sprite={item.sprite}
              style={{
                backgroundImage: `url(${getSpritesheetPath(category)})`,
                backgroundPosition: getScaledSpritePosition(item.sprite, spriteScale, category),
                backgroundSize: getSpritesheetSize(category, spriteScale),
                width: `${spriteSize}px`,
                height: `${spriteSize}px`,
                imageRendering: 'pixelated', // Crisp pixel art scaling
                flexShrink: 0,
              }}
            />
          );
        })() : null}
        <span
          className="item-chip-icon"
          style={hasSprite ? { display: 'none' } : {}}
        >
          {item.icon}
        </span>
        {amount !== null && amount > 1 && (
          <span className="item-chip-quantity">{amount > 999 ? '999+' : amount}</span>
        )}
      </div>

      {/* Render tooltip via portal to document body */}
      {tooltipVisible && createPortal(renderTooltip(), document.body)}
    </>
  );
};

// Helper functions for formatting
function formatSlotName(slot) {
  const slotNames = {
    weapon: 'Weapon',
    offhand: 'Off-Hand',
    helmet: 'Helmet',
    top: 'Top',
    bottom: 'Bottom',
    gloves: 'Gloves',
    boots: 'Boots',
    cape: 'Cape',
    belt: 'Belt',
    pendant1: 'Pendant',
    pendant2: 'Pendant',
    ring1: 'Ring',
    ring2: 'Ring',
  };
  return slotNames[slot] || slot;
}

function formatCategoryName(category) {
  const categoryNames = {
    potion: 'Consumable',
    material: 'Material',
    buff: 'Buff Item',
    quest: 'Quest Item',
    misc: 'Miscellaneous',
  };
  return categoryNames[category] || category;
}

function formatStatName(stat) {
  const statNames = {
    health: 'HP',
    attack: 'ATK',
    defense: 'DEF',
    speed: 'SPD',
    critChance: 'Crit Rate',
    critDamage: 'Crit Dmg',
    range: 'Range',
  };
  return statNames[stat] || stat;
}

function formatDuration(seconds) {
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min${minutes > 1 ? 's' : ''}`;
  }
  return `${seconds}s`;
}

// Quality color coding (Lost Ark style)
function getQualityColor(quality) {
  if (quality >= 90) return '#f59e0b'; // Gold - Excellent (90-100)
  if (quality >= 70) return '#3b82f6'; // Blue - High (70-89)
  if (quality >= 50) return '#10b981'; // Green - Medium (50-69)
  if (quality >= 30) return '#9ca3af'; // Gray - Low (30-49)
  return '#ef4444'; // Red - Poor (0-29)
}

// Quality label
function getQualityLabel(quality) {
  if (quality >= 90) return '⭐ Excellent';
  if (quality >= 70) return '▲ High';
  if (quality >= 50) return '● Medium';
  if (quality >= 30) return '▼ Low';
  return '✖ Poor';
}

// Get equipment type (weapon type, armor type, etc.)
function getEquipmentType(item) {
  // If item has explicit weaponType or equipmentType
  if (item.weaponType) return item.weaponType;
  if (item.equipmentType) return item.equipmentType;

  // Infer from item ID or name patterns
  const name = item.name?.toLowerCase() || '';
  const id = item.id?.toLowerCase() || '';

  // Weapons
  if (item.slot === 'weapon') {
    if (name.includes('dagger') || id.includes('dagger')) return 'One-Handed Dagger';
    if (name.includes('sword') || id.includes('sword')) {
      if (name.includes('great') || name.includes('two')) return 'Two-Handed Sword';
      return 'One-Handed Sword';
    }
    if (name.includes('bow') || id.includes('bow')) return 'Two-Handed Bow';
    if (name.includes('staff') || id.includes('staff')) return 'Two-Handed Staff';
    if (name.includes('wand') || id.includes('wand')) return 'One-Handed Wand';
    if (name.includes('hammer') || id.includes('hammer')) return 'Two-Handed Hammer';
    if (name.includes('axe') || id.includes('axe')) return 'Two-Handed Axe';
    if (name.includes('club') || id.includes('club')) return 'One-Handed Club';
    if (name.includes('spear') || id.includes('spear')) return 'Two-Handed Spear';
    return 'Weapon';
  }

  // Shields
  if (item.slot === 'offhand') {
    if (name.includes('shield') || id.includes('shield')) return 'Shield';
    return 'Off-hand';
  }

  // Armor types by slot
  const armorTypeMap = {
    helmet: 'Hat',
    top: 'Top',
    bottom: 'Bottom',
    overall: 'Overall',
    gloves: 'Gloves',
    boots: 'Shoes',
    cape: 'Cape',
    belt: 'Belt',
    ring1: 'Ring',
    ring2: 'Ring',
    pendant1: 'Pendant',
    pendant2: 'Pendant',
  };

  return armorTypeMap[item.slot] || 'Equipment';
}

// Potential grade colors (Cubing system)
function getPotentialGradeColor(grade) {
  const colors = {
    rare: '#3b82f6',       // Blue
    epic: '#8b5cf6',       // Purple
    unique: '#f59e0b',     // Amber/Gold
    legendary: '#22c55e',  // Green
  };
  return colors[grade] || '#6b7280';
}

// Potential grade icons
function getPotentialGradeIcon(grade) {
  const icons = {
    rare: '🔵',
    epic: '🟣',
    unique: '🟡',
    legendary: '🟢',
  };
  return icons[grade] || '⚪';
}

// Format potential line for display
function formatPotentialLineDisplay(line) {
  const statLabels = {
    STR: 'STR',
    DEX: 'DEX',
    INT: 'INT',
    LUK: 'LUK',
    attack: 'ATK',
    defense: 'DEF',
    health: 'HP',
    allStats: 'All Stats',
    critChance: 'Crit Chance',
    critDamage: 'Crit Damage',
    bossDamage: 'Boss Damage',
    ignoreDef: 'Ignore DEF',
  };

  const label = statLabels[line.stat] || line.stat;

  if (line.type === 'percent') {
    return `+${line.value}% ${label}`;
  } else {
    // Flat bonuses (for crit stats, show as percentage)
    if (line.stat === 'critChance' || line.stat === 'critDamage' || line.stat === 'bossDamage' || line.stat === 'ignoreDef') {
      return `+${Math.round(line.value * 100)}% ${label}`;
    }
    return `+${line.value} ${label}`;
  }
}

export default ItemChip;
